import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Box, Typography, Switch, Snackbar, Alert, Button, TextField, Avatar, Grid, Divider } from '@mui/material';
import axios from '../axios';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import PersonIcon from '@mui/icons-material/Person';
import EventIcon from '@mui/icons-material/Event';
import SaveIcon from '@mui/icons-material/Save';

const PresenzeDinoAlunniDetail = forwardRef(({ idCorso, idLezione, numLezione, statoLezione, dataLezione, onSaveSuccess, onCancel, onDirtyChange }, ref) => {
    const [students, setStudents] = useState([]);
    const [initialStudents, setInitialStudents] = useState([]);
    const [initialDate, setInitialDate] = useState(dataLezione ? dayjs(dataLezione) : null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');
    const [selectedDate, setSelectedDate] = useState(dataLezione ? dayjs(dataLezione) : null);
    const [isDirty, setIsDirty] = useState(false);

    useImperativeHandle(ref, () => ({
        save: handleSave
    }));

    const fetchStudents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/datahandler/presenze-insegnante', {
                token,
                idCorso,
                idLezione,
                numLezione,
                statoLezione
            });
            const mappedStudents = response.data.map(student => ({
                ...student,
                presente: statoLezione == 0 ? true : student.Presente
            }));
            setStudents(mappedStudents);
            setInitialStudents(JSON.parse(JSON.stringify(mappedStudents)));
            setIsDirty(false);
            if (onDirtyChange) onDirtyChange(false);
        } catch (error) {
            console.error('Errore nel recupero degli studenti:', error);
        }
    };

    useEffect(() => {
        fetchStudents();
        const date = dataLezione ? dayjs(dataLezione) : null;
        setSelectedDate(date);
        setInitialDate(date);
    }, [idCorso, numLezione, statoLezione, dataLezione]);

    const checkIfDirty = (currentStudents, currentDate) => {
        const studentsChanged = currentStudents.some((s, idx) => s.presente !== initialStudents[idx]?.presente);
        const dateChanged = !dayjs(currentDate).isSame(initialDate, 'day');
        const dirty = studentsChanged || dateChanged;
        setIsDirty(dirty);
        if (onDirtyChange) onDirtyChange(dirty);
        return dirty;
    };

    const handleAttendanceToggle = (idAlunno) => {
        setStudents(prevStudents => {
            const updated = prevStudents.map(student =>
                student.idAlunno === idAlunno ? { ...student, presente: !student.presente } : student
            );
            checkIfDirty(updated, selectedDate);
            return updated;
        });
    };

    const handleDateChange = (newValue) => {
        setSelectedDate(newValue);
        checkIfDirty(students, newValue);
    };

    const handleSave = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/datahandler/save-presenze', {
                token,
                idCorso,
                idLezione,
                numLezione,
                nuovaData: selectedDate.format('YYYY-MM-DD'),
                students: students.map(student => ({
                    idAlunno: student.idAlunno,
                    presente: student.presente ? 1 : 0
                }))
            });
            setSnackbarMessage('Presenze salvate con successo!');
            setSnackbarSeverity('success');
            setOpenSnackbar(true);
            setIsDirty(false);
            if (onDirtyChange) onDirtyChange(false);
            onSaveSuccess(); // This now just refreshes the list in the background
        } catch (error) {
            console.error('Errore nel salvataggio delle presenze:', error);
            setSnackbarMessage('Errore nel salvataggio delle presenze.');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        }
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setOpenSnackbar(false);
    };

    return (
        <Box sx={{ width: '100%', mx: 'auto' }}>
            {/* Header & Date Selector in a Glass Container */}
            <Box sx={{
                p: 3,
                mb: 3,
                background: 'rgba(255, 255, 255, 0.4)',
                borderRadius: '16px',
                border: '1px solid rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
            }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: '#333' }}>
                        Registro presenze - Lezione {numLezione}
                    </Typography>
                    <Button
                        variant="contained"
                        className={`btn-3d ${(!selectedDate || !selectedDate.isValid()) ? 'btn-3d-grey' : 'btn-3d-orange'}`}
                        onClick={handleSave}
                        disabled={!selectedDate || !selectedDate.isValid()}
                        sx={{
                            px: 4,
                            height: 40,
                            borderRadius: '10px',
                            fontSize: '0.9rem',
                            minWidth: '100px',
                            '&.Mui-disabled': {
                                backgroundColor: '#ccc',
                                color: '#888',
                                boxShadow: '0 4px 0 #999'
                            }
                        }}
                    >
                        Salva
                    </Button>
                </Box>

                <Box sx={{ width: '100%' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="it">
                        <DatePicker
                            label="Seleziona Data Lezione"
                            maxDate={dayjs()}
                            value={selectedDate}
                            onChange={handleDateChange}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    variant: 'outlined',
                                    size: 'small',
                                    sx: { bgcolor: 'rgba(255,255,255,0.5)', borderRadius: '8px' }
                                }
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            </Box>

            {/* Students List */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                {students.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'rgba(0,0,0,0.03)', borderRadius: '12px' }}>
                        <Typography variant="body1" color="text.secondary">Nessun alunno trovato.</Typography>
                    </Box>
                ) : (
                    students.map((student) => (
                        <Box
                            key={student.idAlunno}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                p: 1.5,
                                background: student.presente
                                    ? 'rgba(255, 255, 255, 0.5)'
                                    : 'rgba(255, 82, 82, 0.15)',
                                borderRadius: '12px',
                                border: '1px solid rgba(255, 255, 255, 0.5)',
                                backdropFilter: 'blur(5px)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'translateX(4px)',
                                    background: student.presente
                                        ? 'rgba(255, 255, 255, 0.8)'
                                        : 'rgba(255, 82, 82, 0.25)',
                                }
                            }}
                        >
                            <Avatar sx={{
                                bgcolor: student.presente ? 'rgba(0,0,0,0.05)' : 'rgba(255,82,82,0.6)',
                                color: student.presente ? 'inherit' : 'white',
                                mr: 2
                            }}>
                                <PersonIcon />
                            </Avatar>

                            <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                    sx={{
                                        fontWeight: 700,
                                        textDecoration: student.presente ? 'none' : 'line-through',
                                        color: student.presente ? 'inherit' : 'rgba(0,0,0,0.5)',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    {student.first_name} {student.last_name}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontWeight: 700,
                                        color: student.presente ? '#2e7d32' : '#d32f2f',
                                        minWidth: 60,
                                        textAlign: 'right'
                                    }}
                                >
                                    {student.presente ? 'Presente' : 'Assente'}
                                </Typography>
                                <Switch
                                    checked={student.presente}
                                    onChange={() => handleAttendanceToggle(student.idAlunno)}
                                    color="success"
                                    size="small"
                                />
                            </Box>
                        </Box>
                    ))
                )}
            </Box>

            <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleSnackbarClose}>
                <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%', borderRadius: '12px' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
});

export default PresenzeDinoAlunniDetail;
